package com.bitterinfant.webui

import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLMediaElement
import org.w3c.dom.url.URLSearchParams
import kotlin.js.json

/**
 * Deep links of the form `?tab=<tab>&play=<clip>&t=<start>`, e.g. `?tab=samples&play=yusif&t=1:30`:
 * the page opens on that tab, scrolls to that clip, and starts it playing (`t`, optional, takes
 * `45`, `1:30` or `1m30s`). Tab and clip are matched by [slugify], so `?tab=Jed's%20Dead` and
 * `?tab=jeds-dead` are equivalent. A clip's slug is its media component's `anchor` argument if
 * given, otherwise its slugified title/caption — it's also the clip block's DOM `id`. Each clip's
 * [CopyLinkButton] builds these links, and [Tabs] keeps `?tab=` in the address bar current.
 *
 * Browsers only allow audible autoplay once the visitor has interacted with the site (Safari
 * and Firefox block it outright by default), so playback often won't start on a fresh visit;
 * the clip is still scrolled to and ringed by [revealDeepLinkTarget] so its play button is obvious.
 */
object DeepLink {
    private val params = URLSearchParams(window.location.search)
    private var pendingClip: String? = params.get("play")?.let(::slugify)?.takeIf { it.isNotEmpty() }

    /** Seconds into the `?play=` clip to start from (`&t=`), or null to start at the beginning. */
    val startSeconds: Int? = params.get("t")?.let(::parseTime)?.takeIf { it > 0 }

    /** Index of the `?tab=` label in [labels], or 0 if there's no (matching) `tab` parameter. */
    fun tabIndex(labels: List<String>): Int {
        val tab = params.get("tab")?.let(::slugify) ?: return 0
        return labels.indexOfFirst { slugify(it) == tab }.coerceAtLeast(0)
    }

    /**
     * True if [anchor] is the `?play=` clip and it hasn't been claimed yet. Only the first call
     * for the target returns true, so revisiting its tab later doesn't scroll or replay it.
     */
    fun claim(anchor: String): Boolean {
        if (pendingClip != anchor) return false
        pendingClip = null
        return true
    }

    /** Absolute link that opens [tab] (a slug; null for the default tab) at clip [anchor]. */
    fun linkTo(tab: String?, anchor: String, startSeconds: Int = 0): String {
        val link = URLSearchParams()
        tab?.let { link.set("tab", it) }
        link.set("play", anchor)
        if (startSeconds > 0) link.set("t", startSeconds.toString())
        return "${window.location.origin}${window.location.pathname}?$link"
    }

    /**
     * Shows [tabLabel] as `?tab=` in the address bar, so a copied URL reopens that tab. Drops the
     * already-handled `play`/`t`, keeps any other parameters, and adds no history entry.
     */
    fun showTab(tabLabel: String) {
        val query = URLSearchParams(window.location.search)
        query.set("tab", slugify(tabLabel))
        query.delete("play")
        query.delete("t")
        window.history.replaceState(null, "", "?$query${window.location.hash}")
    }
}

/** "45" → 45, "1:30" → 90, "1m30s" → 90; null if it's none of those. */
private fun parseTime(raw: String): Int? {
    val text = raw.trim().lowercase()
    text.toIntOrNull()?.let { return it }
    Regex("""(\d+):(\d\d?)""").matchEntire(text)?.let {
        val (m, s) = it.destructured
        return m.toInt() * 60 + s.toInt()
    }
    Regex("""(?:(\d+)m)?(?:(\d+)s)?""").matchEntire(text)?.takeIf { text.isNotEmpty() }?.let {
        val (m, s) = it.destructured
        return (m.toIntOrNull() ?: 0) * 60 + (s.toIntOrNull() ?: 0)
    }
    return null
}

/** 90 → "1:30". */
internal fun formatTime(seconds: Int): String = "${seconds / 60}:${(seconds % 60).toString().padStart(2, '0')}"

/** "Jed's Dead" → "jeds-dead", "Closed Eyes (Short Film)" → "closed-eyes-short-film". */
fun slugify(text: String): String =
    text.lowercase().replace("'", "").replace(Regex("[^a-z0-9]+"), "-").trim('-')

/**
 * Reveals a deep-linked <audio>/<video> (its parent is the clip's block), seeks to the link's
 * `&t=` if any, and tries to play it.
 */
internal fun playDeepLinkTarget(media: HTMLMediaElement) {
    media.parentElement?.let(::revealDeepLinkTarget)
    DeepLink.startSeconds?.let { start ->
        // With preload="metadata" the duration usually isn't known yet, and seeking before then
        // isn't reliable everywhere, so wait for it.
        if (media.readyState >= HTMLMediaElement.HAVE_METADATA) media.currentTime = start.toDouble()
        else media.addEventListener("loadedmetadata", { media.currentTime = start.toDouble() }, json("once" to true))
    }
    // Rejects when the browser blocks autoplay; the visitor then just presses the ringed player.
    media.play().catch { }
}

/** Scrolls a deep-linked clip's block to the middle of the screen and briefly rings it. */
internal fun revealDeepLinkTarget(block: Element) {
    block.scrollIntoView(json("behavior" to "smooth", "block" to "center"))
    // Web Animations API, so the ring needs no stylesheet and leaves no styles behind. It holds
    // through the smooth scroll, then fades.
    fun ring(color: String, offset: Double) = json(
        "outline" to "2px solid $color", "outlineOffset" to "8px", "borderRadius" to "12px", "offset" to offset,
    )
    block.asDynamic().animate(
        arrayOf(ring(Theme.accent, 0.0), ring(Theme.accent, 0.5), ring("transparent", 1.0)),
        json("duration" to 3500, "easing" to "ease-out"),
    )
}
